import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import WhatsAppButton from './WhatsAppButton';
import { 
  Phone, 
  PhoneCall, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Calendar,
  User,
  Users,
  MapPin,
  Award,
  MessageSquare,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const MatchAgenzia = ({ agencyId }) => {
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('tutti');
  const [showCallModal, setShowCallModal] = useState(false);
  const [showEsitoModal, setShowEsitoModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [callNotes, setCallNotes] = useState('');

  // Carica matches dell'agenzia
  useEffect(() => {
    loadMatches();
    loadStats();
  }, [agencyId]);

  // Filtri matches quando cambia il filtro attivo
  useEffect(() => {
    filterMatches();
  }, [matches, activeFilter]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API}/matches?agency_id=${agencyId}`, { headers });
      if (response.ok) {
        const matchesData = await response.json();
        console.log('Matches caricati:', matchesData);
        setMatches(matchesData);
      } else {
        console.error('Errore response:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Errore nel caricamento matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API}/matches/stats?agency_id=${agencyId}`, { headers });
      if (response.ok) {
        const statsData = await response.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Errore nel caricamento stats:', error);
    }
  };

  const filterMatches = () => {
    let filtered = matches.filter(match => match.status !== 'archiviato');

    switch (activeFilter) {
      case 'assegnati':
        // Mostra SOLO i match espliciti (creati dall'admin)
        filtered = filtered.filter(match => match.match_type === 'esplicito');
        break;
      case 'vicini':
        filtered = filtered.filter(match => match.affinita_geografica === 100);
        break;
      case 'limitrofi':
        filtered = filtered.filter(match => match.affinita_geografica === 75 || match.affinita_geografica === 50);
        break;
      case 'da_chiamare':
        filtered = filtered.filter(match => match.status === 'in_attesa_contatto' && match.match_type === 'esplicito');
        break;
      case 'colloquio_fissato':
        filtered = filtered.filter(match => match.status === 'colloquio_fissato');
        break;
      case 'inseriti':
        filtered = filtered.filter(match => match.status === 'inserito');
        break;
      default:
        // "tutti" mostra solo i match espliciti di default
        filtered = filtered.filter(match => match.match_type === 'esplicito');
        break;
    }
    setFilteredMatches(filtered);
  };

  const handleCallClick = (match) => {
    setSelectedMatch(match);
    setShowCallModal(true);
  };

  const handleSegnaChiamato = async (match) => {
    setSelectedMatch(match);
    setShowEsitoModal(true);
  };

  const handleEsitoSubmit = async (esito) => {
    try {
      let newStatus = 'chiamato';
      let scheduledAt = null;
      
      if (esito === 'interessato') {
        newStatus = 'colloquio_fissato';
        // Qui potresti aprire un modal per la data del colloquio
        const domani = new Date();
        domani.setDate(domani.getDate() + 1);
        scheduledAt = domani.toISOString();
      } else if (esito === 'non_interessato') {
        newStatus = 'non_interessato';
      }
      
      const updateData = {
        status: newStatus,
        ...(scheduledAt && { scheduled_at: scheduledAt }),
        ...(callNotes && { note: callNotes })
      };

      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/matches/${selectedMatch.id}/status`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await loadMatches(); // Ricarica i dati
        await loadStats();
        setShowEsitoModal(false);
        setCallNotes('');
      }
    } catch (error) {
      console.error('Errore aggiornamento esito:', error);
    }
  };

  const handleAddNote = async (matchId, note) => {
    try {
      const response = await fetch(`${API}/matches/${matchId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: note,
          role: 'agency'
        })
      });

      if (response.ok) {
        await loadMatches();
      }
    } catch (error) {
      console.error('Errore aggiunta nota:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'in_attesa_contatto': { color: 'bg-yellow-100 text-yellow-800', text: 'Da chiamare' },
      'chiamato': { color: 'bg-blue-100 text-blue-800', text: 'Chiamato' },
      'colloquio_fissato': { color: 'bg-green-100 text-green-800', text: 'Colloquio fissato' },
      'inserito': { color: 'bg-purple-100 text-purple-800', text: 'Inserito' },
      'non_interessato': { color: 'bg-red-100 text-red-800', text: 'Non interessato' }
    };
    
    const statusInfo = statusMap[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <Badge className={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  const filters = [
    { key: 'tutti', label: 'Match Assegnati', icon: Users },
    { key: 'da_chiamare', label: 'Da chiamare', icon: Phone },
    { key: 'colloquio_fissato', label: 'Colloquio fissato', icon: Calendar },
    { key: 'inseriti', label: 'Inseriti', icon: CheckCircle },
    { key: 'vicini', label: 'Candidati Vicini', icon: MapPin },
    { key: 'limitrofi', label: 'Zone Limitrofe', icon: Award }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{stats.total_matches || 0}</div>
            <div className="text-sm opacity-90">Match Totali</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{stats.contacted || 0}</div>
            <div className="text-sm opacity-90">Contattati</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{stats.interviews_scheduled || 0}</div>
            <div className="text-sm opacity-90">Colloqui</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{stats.inserted || 0}</div>
            <div className="text-sm opacity-90">Inseriti</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => {
          const IconComponent = filter.icon;
          return (
            <Button
              key={filter.key}
              variant={activeFilter === filter.key ? "default" : "outline"}
              onClick={() => setActiveFilter(filter.key)}
              className={`flex items-center gap-2 rounded-xl ${
                activeFilter === filter.key
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'hover:bg-blue-50'
              }`}
            >
              <IconComponent className="w-4 h-4" />
              {filter.label}
            </Button>
          );
        })}
      </div>

      {/* Lista Matches - Tabella */}
      <div className="overflow-x-auto">
        {filteredMatches.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-gray-500">
              <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun match trovato per questo filtro.</p>
            </div>
          </Card>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-semibold">Candidato</th>
                <th className="text-left p-3 font-semibold">Data Colloquio</th>
                <th className="text-left p-3 font-semibold">Stato</th>
                <th className="text-left p-3 font-semibold">Note</th>
                <th className="text-left p-3 font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map((match) => (
                <tr key={match.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {match.candidate_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <p className="font-semibold">{match.candidate_name}</p>
                        <div className="flex gap-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {match.telefono || match.candidate_phone}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {match.candidato?.zona_preferenza_lavorativa || match.candidato?.citta || match.candidate_city || 'N/D'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    {match.interview_date ? (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-green-600" />
                        <span className="text-sm">
                          {new Date(match.interview_date).toLocaleDateString('it-IT', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Non programmato</span>
                    )}
                  </td>
                  <td className="p-3">{getStatusBadge(match.status)}</td>
                  <td className="p-3">
                    {match.agency_note ? (
                      <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
                        <MessageSquare className="w-3 h-3 mt-0.5" />
                        <div>{match.agency_note}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <WhatsAppButton
                        telefono={match.telefono || match.candidate_phone}
                        nome={match.candidate_name}
                        tipo="candidato"
                        className="rounded-xl"
                      />
                      {match.status === 'in_attesa_contatto' && (
                        <Button
                          size="sm"
                          onClick={() => handleSegnaChiamato(match)}
                          variant="outline"
                          className="rounded-xl flex items-center gap-2"
                        >
                          <PhoneCall className="w-3 h-3" />
                          Segna chiamato
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Chiama Candidato */}
      {showCallModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Chiama {selectedMatch.candidate_name}</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="text-sm text-gray-600">Numero di telefono:</div>
                <div className="text-lg font-semibold">+39 333 1234567</div> {/* TODO: Get real phone */}
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Note per la chiamata:</label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  className="w-full mt-1 p-3 border rounded-xl resize-none"
                  rows="3"
                  placeholder="Aggiungi note sulla chiamata..."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => setShowCallModal(false)}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Chiudi
              </Button>
              <Button
                onClick={() => {
                  setShowCallModal(false);
                  handleSegnaChiamato(selectedMatch);
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-xl"
              >
                Segna come chiamato
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Esito Chiamata */}
      {showEsitoModal && selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Esito chiamata - {selectedMatch.candidate_name}</h3>
            
            <div className="space-y-3">
              <Button
                onClick={() => handleEsitoSubmit('interessato')}
                className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl flex items-center justify-center gap-2 p-3"
              >
                <CheckCircle className="w-5 h-5" />
                ✅ Interessato → Imposta colloquio
              </Button>
              
              <Button
                onClick={() => handleEsitoSubmit('da_richiamare')}
                variant="outline"
                className="w-full rounded-xl flex items-center justify-center gap-2 p-3"
              >
                <Clock className="w-5 h-5" />
                ⏳ Da richiamare
              </Button>
              
              <Button
                onClick={() => handleEsitoSubmit('non_interessato')}
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50 rounded-xl flex items-center justify-center gap-2 p-3"
              >
                <XCircle className="w-5 h-5" />
                ❌ Non interessato
              </Button>
            </div>

            <div className="mt-4">
              <label className="text-sm text-gray-600">Note interne (facoltativo):</label>
              <textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                className="w-full mt-1 p-3 border rounded-xl resize-none"
                rows="2"
                placeholder="Aggiungi note interne..."
              />
            </div>

            <Button
              onClick={() => {
                setShowEsitoModal(false);
                setCallNotes('');
              }}
              variant="outline"
              className="w-full mt-4 rounded-xl"
            >
              Annulla
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchAgenzia;